import { useCallback, useEffect, useRef, useState } from 'react';
import type { ILink } from '@platform/core/types';
import { dao } from '@platform/dao';
import type { IGraphEdge } from './IGraphEdge';
import type { IGraphNode } from './IGraphNode';
import type { IRelationsGraph } from './IRelationsGraph';

/** Скільки вузлів рівня розкривається далі й скільки нових вузлів додається на рівні (читабельність графа). */
const EXPAND = [Infinity, 16, 12];
const ADD = [Infinity, 40, 24, 24];

const nodeOf = (l: ILink, level: number, parent: string): IGraphNode => ({
  id: l.otherRecordId, module: l.otherModule, recordType: l.otherRecordType, display: l.otherDisplay, title: l.otherTitle, status: l.otherStatus, level, parent,
});

/**
 * Граф зв'язків навколо запису (прототип v4, навігатор): обхід у ширину на глибину 1–3 через GET /records/{id}/links (зв'язки
 * кешуються на час відкриття), з обмеженням вузлів на рівень; ребра між уже відомими вузлами теж показуються. `reload` — після змін.
 */
export function useRelationsGraph(center: IGraphNode, depth: number): IRelationsGraph & { reload: () => void } {
  const cache = useRef(new Map<string, ILink[]>());
  const [graph, setGraph] = useState<IRelationsGraph>({ nodes: [center], edges: [], loading: true, truncated: false });
  const [version, setVersion] = useState(0);
  const linksOf = useCallback(async (id: string) => {
    const hit = cache.current.get(id);
    if (hit) return hit;
    const links = await dao.collaboration.links(id).catch(() => [] as ILink[]);
    cache.current.set(id, links);
    return links;
  }, []);
  useEffect(() => {
    let alive = true;
    void (async () => {
      setGraph((g) => ({ ...g, loading: true }));
      const nodes = new Map<string, IGraphNode>([[center.id, { ...center, level: 0, parent: undefined }]]);
      const edges = new Map<string, IGraphEdge>();
      let frontier = [center.id];
      let truncated = false;
      for (let level = 1; level <= depth; level += 1) {
        const expand = frontier.slice(0, EXPAND[level - 1]);
        truncated ||= expand.length < frontier.length;
        const next: string[] = [];
        for (const id of expand) {
          for (const l of await linksOf(id)) {
            const key = [id, l.otherRecordId].sort().join('|');
            if (!nodes.has(l.otherRecordId)) {
              if (next.length >= ADD[level]!) { truncated = true; continue; }
              nodes.set(l.otherRecordId, nodeOf(l, level, id));
              next.push(l.otherRecordId);
            }
            if (!edges.has(key)) edges.set(key, { from: id, to: l.otherRecordId, type: l.linkType, level });
          }
        }
        frontier = next;
      }
      if (alive) setGraph({ nodes: [...nodes.values()], edges: [...edges.values()], loading: false, truncated });
    })();
    return () => { alive = false; };
  }, [center, depth, linksOf, version]);
  const reload = useCallback(() => { cache.current.clear(); setVersion((v) => v + 1); }, []);
  return { ...graph, reload };
}
